import { ColDef, ColGroupDef } from 'ag-grid-community';
import { ALL_SERVICES, ALL_WEIGHTS_TABS, PlanWeightsTab, Service } from 'src/constants';
import { emptyCellFormatter } from 'src/util/grid/cellFormatter';
import { getPaperPlanBooleanAttributeRenderer } from 'src/util/grid/cellRenderer';

interface PlanWeightsColDef extends ColDef {
    tabs: PlanWeightsTab[];
    services: Service[];
}

interface PlanWeightsColGroupDef extends ColGroupDef {
    tabs: PlanWeightsTab[];
    services: Service[];
}

export const basePlanWeightsColDefs: (PlanWeightsColDef | PlanWeightsColGroupDef)[] = [
    {
        field: 'planId',
        headerName: 'Plan ID',
        filter: 'agSetColumnFilter',
        valueFormatter: emptyCellFormatter,
        minWidth: 300,
        pinned: true,
        tabs: ALL_WEIGHTS_TABS,
        services: ALL_SERVICES
    },
    {
        field: 'planGroup',
        headerName: 'Plan Group',
        filter: 'agSetColumnFilter',
        valueFormatter: emptyCellFormatter,
        minWidth: 200,
        tabs: ALL_WEIGHTS_TABS,
        services: ALL_SERVICES
    },
    {
        field: 'planType',
        headerName: 'Plan Type',
        filter: 'agSetColumnFilter',
        valueFormatter: emptyCellFormatter,
        minWidth: 200,
        tabs: ALL_WEIGHTS_TABS,
        services: ALL_SERVICES
    },
    {
        field: 'isPromotion',
        headerName: 'Promotion',
        cellRenderer: getPaperPlanBooleanAttributeRenderer,
        minWidth: 200,
        tabs: ALL_WEIGHTS_TABS,
        services: ALL_SERVICES
    },
    {
        field: 'customerPays',
        headerName: 'Customer Pays',
        filter: 'agSetColumnFilter',
        cellRenderer: getPaperPlanBooleanAttributeRenderer,
        minWidth: 200,
        tabs: ALL_WEIGHTS_TABS,
        services: ALL_SERVICES
    },
    {
        field: 'royaltyBearing',
        headerName: 'Royalty Bearing',
        filter: 'agSetColumnFilter',
        cellRenderer: getPaperPlanBooleanAttributeRenderer,
        minWidth: 200,
        tabs: ALL_WEIGHTS_TABS,
        services: ALL_SERVICES
    },
    {
        field: 'amuBlendedPsm',
        headerName: 'AMU Blended PSM Calc',
        filter: 'agSetColumnFilter',
        valueFormatter: emptyCellFormatter,
        minWidth: 250,
        tabs: [PlanWeightsTab.WMG],
        services: [Service.Unlimited]
    },
    {
        headerName: 'AMU Performance Discount',
        children: [
            {
                field: 'amuDiscount1PreSpatial',
                headerName: 'Discount 1 & 2 Pre-Spatial',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                minWidth: 250
            },
            {
                field: 'amuDiscount2PreSpatial',
                headerName: 'Discount 1 & 2 Pre-Spatial (ASD Tier 2)',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                minWidth: 350
            },
            {
                field: 'amuDiscount1PostSpatial',
                headerName: 'Discount 1 & 2 Post-Spatial',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                minWidth: 250
            },
            {
                field: 'amuDiscount2PostSpatial',
                headerName: 'Discount 1 & 2 Post-Spatial (ASD Tier 2)',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                minWidth: 350
            },
            {
                field: 'amuDiscount3',
                headerName: 'Discount 3',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                minWidth: 150
            },
            {
                field: 'amuDiscount4Ft',
                headerName: 'Discount 4 FT',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                minWidth: 150
            },
            {
                field: 'amuDiscount4Amu',
                headerName: 'Discount 4 AMU',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                minWidth: 200
            }
        ],
        tabs: [PlanWeightsTab.UMG],
        services: [Service.Unlimited]
    },
    {
        headerName: 'Prime Primary',
        children: [
            {
                field: 'primePrimaryNumerator',
                headerName: 'Numerator',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                minWidth: 150
            },
            {
                field: 'primePrimaryDenominator',
                headerName: 'Denominator',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                minWidth: 150
            }
        ],
        tabs: [PlanWeightsTab.UMG, PlanWeightsTab.WMG, PlanWeightsTab.SONY, PlanWeightsTab.ORCHARD],
        services: [Service.Prime]
    },
    {
        headerName: 'Prime Secondary',
        children: [
            {
                field: 'primeSecondaryNumerator',
                headerName: 'Numerator',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                minWidth: 150
            },
            {
                field: 'primeSecondaryDenominator',
                headerName: 'Denominator',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                minWidth: 150
            }
        ],
        tabs: [PlanWeightsTab.WMG],
        services: [Service.Prime]
    },
    {
        headerName: 'AMF Conversion',
        children: [
            {
                field: 'amfConversionNumerator',
                headerName: 'Numerator',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                minWidth: 150
            },
            {
                field: 'amfConversionDenominator',
                headerName: 'Denominator',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                minWidth: 150
            }
        ],
        tabs: [PlanWeightsTab.SONY, PlanWeightsTab.UMG, PlanWeightsTab.WMG],
        services: [Service.Free]
    },
    {
        headerName: 'AMU Alexa PSM',
        children: [
            {
                field: 'amuAlexaPsmNumerator',
                headerName: 'Numerator',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                minWidth: 150
            },
            {
                field: 'amuAlexaPsmDenominator',
                headerName: 'Denominator',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                minWidth: 150
            }
        ],
        tabs: ALL_WEIGHTS_TABS.filter((tab) => tab !== PlanWeightsTab.WMG),
        services: [Service.Unlimited]
    }
];
